import React, { useState, useMemo } from 'react';
import { getStudentFinancialStatus } from '../../../api/students';
import styles from '../../../styles/EstudiantesStyles/Estudiantes.module.css';

function EstadoFinanciero({ student }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [estadoCuenta, setEstadoCuenta] = React.useState(null);
  const [expandedCiclos, setExpandedCiclos] = useState({});
  const [expandedFolios, setExpandedFolios] = useState({});

  const formatCurrency = (amount) => {
    const value = parseFloat(amount) || 0;
    return `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    return dateStr;
  };

  React.useEffect(() => {
    const fetchEstadoCuenta = async () => {
      if (!student?.id_estudiante && !student?.id) return;
      
      const studentId = student.id_estudiante || student.id;
      setLoading(true);
      setError(null);
      
      try {
        const data = await getStudentFinancialStatus(studentId);
        setEstadoCuenta(data);
      } catch (err) {
        console.error('Error al obtener estado de cuenta:', err);
        setError('No se pudo cargar la información financiera');
      } finally {
        setLoading(false);
      }
    };

    fetchEstadoCuenta();
  }, [student]);

  // Parsea "2026-2", "2026-03" → { anio, periodo }
  const parseCiclo = (nombre) => {
    const match = String(nombre || '').match(/^(\d{4})[^0-9](\d+)$/);
    if (!match) return { anio: 0, periodo: 0 };
    return { anio: parseInt(match[1]), periodo: parseInt(match[2]) };
  };

  const historialPagos = estadoCuenta?.historial_pagos || [];

  // Agrupación: ciclo → folio → pagos (debe estar antes de los early returns)
  const pagosPorCiclo = useMemo(() => {
    const ciclosMap = new Map();
    historialPagos.forEach((pago) => {
      const cicloKey = pago.id_ciclo_escolar ?? 'sin_ciclo';
      const cicloNombre = pago.ciclo_nombre || (pago.id_ciclo_escolar ? `Ciclo ${pago.id_ciclo_escolar}` : 'Sin Ciclo');
      if (!ciclosMap.has(cicloKey)) {
        ciclosMap.set(cicloKey, { id_ciclo_escolar: cicloKey, ciclo_nombre: cicloNombre, totalMonto: 0, foliosMap: new Map() });
      }
      const ciclo = ciclosMap.get(cicloKey);
      const folio = pago.folio_recibo || pago.folio || 'SIN FOLIO';
      const monto = (parseFloat(pago.monto_aplicado || 0) || 0) + (parseFloat(pago.monto_recargo || 0) || 0);
      if (!ciclo.foliosMap.has(folio)) {
        ciclo.foliosMap.set(folio, { folio, pagos: [], totalMonto: 0, formaPago: pago.forma_pago || '-', estado: pago.estado_pago || 'APLICADO' });
      }
      const grupo = ciclo.foliosMap.get(folio);
      grupo.totalMonto += isNaN(monto) ? 0 : monto;
      grupo.pagos.push(pago);
      ciclo.totalMonto += isNaN(monto) ? 0 : monto;
    });
    return Array.from(ciclosMap.values())
      .map((c) => ({ ...c, gruposFolio: Array.from(c.foliosMap.values()) }))
      .sort((a, b) => {
        if (a.id_ciclo_escolar === 'sin_ciclo') return 1;
        if (b.id_ciclo_escolar === 'sin_ciclo') return -1;
        const ca = parseCiclo(a.ciclo_nombre);
        const cb = parseCiclo(b.ciclo_nombre);
        if (cb.anio !== ca.anio) return cb.anio - ca.anio;
        return cb.periodo - ca.periodo;
      });
  }, [historialPagos]);

  const toggleCiclo = (key) => setExpandedCiclos((p) => ({ ...p, [key]: !p[key] }));
  const toggleFolio = (folio) => setExpandedFolios((p) => ({ ...p, [folio]: !p[folio] }));

  if (loading) {
    return (
      <div className={styles.tabPanel}>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
          <p>Cargando información financiera...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabPanel}>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#dc3545' }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const resumen = estadoCuenta?.resumen || {};
  const cargosPendientes = estadoCuenta?.cargos_pendientes || [];

  return (
    <div className={styles.tabPanel}>
      {/* Resumen Financiero */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="5" width="20" height="14" rx="2" stroke="#ed8b00" strokeWidth="2"/>
            <path d="M2 10H22" stroke="#ed8b00" strokeWidth="2"/>
          </svg>
          <h3 className={styles.sectionTitle}>Resumen Financiero</h3>
        </div>
        <div className={styles.financialCards}>
          <div className={styles.financialCard}>
            <div className={styles.financialCardLabel}>Saldo a Favor</div>
            <div className={styles.financialCardValue} style={{ color: '#28a745' }}>
              {formatCurrency(0)}
            </div>
          </div>
          <div className={styles.financialCard}>
            <div className={styles.financialCardLabel}>Próximo Pago</div>
            <div className={styles.financialCardValue}>
              {formatCurrency(resumen.proximo_vencimiento || 0)}
            </div>
            {resumen.proxima_fecha_vencimiento && resumen.proxima_fecha_vencimiento !== 'N/A' && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Vence: {resumen.proxima_fecha_vencimiento}
              </div>
            )}
          </div>
          <div className={styles.financialCard}>
            <div className={styles.financialCardLabel}>Adeudos</div>
            <div className={styles.financialCardValue} style={{ color: resumen.saldo_total > 0 ? '#dc3545' : '#28a745' }}>
              {formatCurrency(resumen.saldo_total || 0)}
            </div>
            {resumen.saldo_vencido > 0 && (
              <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
                Vencido: {formatCurrency(resumen.saldo_vencido)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Movimientos Financieros (Cargos Pendientes) */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3V21H21" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 16L12 11L16 15L21 10" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3 className={styles.sectionTitle}>Movimientos Financieros</h3>
        </div>
        <div className={styles.financialTable}>
          {cargosPendientes.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Ciclo</th>
                  <th>Concepto</th>
                  <th>Fecha de Vencimiento</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {cargosPendientes.map((cargo, index) => (
                  <tr key={cargo.id || index}>
                    <td>{cargo.ciclo_nombre || cargo.id_ciclo_escolar || '-'}</td>
                    <td>{cargo.concepto || 'N/A'}</td>
                    <td>{formatDate(cargo.fecha_vencimiento)}</td>
                    <td>{formatCurrency(cargo.monto_neto || 0)}</td>
                    <td>
                      <span className={
                        cargo.estado === 'PAGADO' ? styles.estadoBadgeSaldado :
                        cargo.estado === 'PENDIENTE' ? styles.estadoBadgePendiente :
                        cargo.estado === 'VENCIDO' ? styles.estadoBadgeVencido :
                        styles.estadoBadgeParcial
                      }>
                        {cargo.estado || 'N/A'}
                      </span>
                    </td>
                    <td className={(cargo.saldo_pendiente || 0) > 0 ? styles.saldoDeuda : styles.saldoOk}>
                      {formatCurrency(cargo.saldo_pendiente || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No hay movimientos financieros disponibles
            </p>
          )}
        </div>
      </div>

      {/* Historial de Pagos */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8V12L15 15" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="9" stroke="#ed8b00" strokeWidth="2"/>
          </svg>
          <h3 className={styles.sectionTitle}>Historial de Pagos</h3>
        </div>
        <div className={styles.financialTable}>
          {historialPagos.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Monto Aplicado</th>
                  <th>Folio Recibo</th>
                  <th>Forma de Pago</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pagosPorCiclo.map((ciclo) => {
                  const cicloExpanded = !!expandedCiclos[ciclo.id_ciclo_escolar];
                  return (
                    <React.Fragment key={ciclo.id_ciclo_escolar}>
                      {/* Fila de ciclo */}
                      <tr
                        className={styles.cicloPagoRow}
                        onClick={() => toggleCiclo(ciclo.id_ciclo_escolar)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td colSpan={7}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24"
                              style={{ transition: 'transform 0.2s', transform: cicloExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}>
                              <path d="M8 10L12 14L16 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span style={{ fontWeight: 600, color: 'white' }}>{ciclo.ciclo_nombre}</span>
                            <span className={styles.cicloBadge}>
                              {ciclo.gruposFolio.length} recibo{ciclo.gruposFolio.length !== 1 ? 's' : ''}
                            </span>
                            <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'white' }}>
                              {formatCurrency(ciclo.totalMonto)}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {/* Fila de encabezados cuando se expande el ciclo */}
                      {cicloExpanded && (
                        <tr style={{ backgroundColor: '#f0f4f8', borderBottom: '1px solid #d0d8e0' }}>
                          <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}></th>
                          <th style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>Fecha</th>
                          <th style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>Concepto</th>
                          <th style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>Monto Aplicado</th>
                          <th style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>Folio Recibo</th>
                          <th style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>Forma de Pago</th>
                          <th style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>Estado</th>
                        </tr>
                      )}

                      {/* Filas de folios dentro del ciclo */}
                      {cicloExpanded && ciclo.gruposFolio.map((grupo, gi) => {
                        const conceptosUnicos = Array.from(new Set(grupo.pagos.map((p) => p.concepto || '-')));
                        const conceptosExtra = Math.max(conceptosUnicos.length - 1, 0);
                        const primerConcepto = conceptosUnicos[0] || '-';
                        const esMultiple = grupo.pagos.length > 1;
                        const expandido = esMultiple && !!expandedFolios[grupo.folio];
                        const esCancelado = grupo.estado === 'CANCELADO';

                        return (
                          <React.Fragment key={grupo.folio || gi}>
                            <tr style={{ backgroundColor: '#ffffff' }}>
                              <td style={{ textAlign: 'center', cursor: esMultiple ? 'pointer' : 'default' }}
                                  onClick={esMultiple ? () => toggleFolio(grupo.folio) : undefined}>
                                {esMultiple && (
                                  <svg width="13" height="13" viewBox="0 0 24 24"
                                    style={{ transition: 'transform 0.2s', transform: expandido ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                                    <path d="M8 10L12 14L16 10" stroke="#4a5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </td>
                              <td>{formatDate(grupo.pagos[0]?.fecha)}</td>
                              <td>
                                {expandido ? (
                                  <span style={{ fontSize: 12, fontWeight: 600, color: '#002f6c' }}>CARGOS DE ESTE RECIBO</span>
                                ) : (
                                  <>
                                    {primerConcepto}
                                    {conceptosExtra > 0 && (
                                      <span style={{ color: '#002f6c', fontWeight: 600 }}>
                                        {` (+ ${conceptosExtra} cargo${conceptosExtra > 1 ? 's' : ''} más)`}
                                      </span>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className={esCancelado ? styles.montoCancelado : styles.montoPositivo}>
                                {formatCurrency(grupo.totalMonto)}
                              </td>
                              <td>{grupo.folio}</td>
                              <td>{grupo.formaPago}</td>
                              <td>
                                <span className={
                                  grupo.estado === 'CANCELADO' ? styles.estadoBadgeCancelado
                                  : grupo.estado === 'APLICADO' || grupo.estado === 'SALDADO' ? styles.estadoBadgeSaldado
                                  : grupo.estado === 'PENDIENTE' ? styles.estadoBadgePendiente
                                  : styles.estadoBadge
                                }>
                                  {grupo.estado}
                                </span>
                              </td>
                            </tr>
                            {esMultiple && expandido && grupo.pagos.map((pago, pi) => {
                              const monto = (parseFloat(pago.monto_aplicado || 0) || 0) + (parseFloat(pago.monto_recargo || 0) || 0);
                              return (
                                <tr key={`${grupo.folio}-${pi}`} style={{ backgroundColor: '#edf2f7' }}>
                                  <td></td>
                                  <td>{formatDate(pago.fecha)}</td>
                                  <td style={{ paddingLeft: 24 }}>
                                    {pago.codigo_concepto ? `${pago.codigo_concepto} - ${pago.concepto || '-'}` : pago.concepto || '-'}
                                  </td>
                                  <td className={pago.estado_pago === 'CANCELADO' ? styles.montoCancelado : styles.montoDetalle}>
                                    {formatCurrency(monto)}
                                  </td>
                                  <td colSpan={3}></td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No hay historial de pagos disponible
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default EstadoFinanciero;
